import {useEffect, useState} from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  TagOutlined,
} from '@ant-design/icons';
import {useHistory, useParams, useLocation} from 'react-router-dom';
import moment from 'moment';

import {DiscountCodeService} from '../DiscountCodeService';
import {useDiscountCodeData} from '../hooks/useDiscountCodeData';
import type {
  NewDiscountCode,
  NewUpdateDiscountCodeInput,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/discountCode/graphql/discountCode.interface';

const {Title, Text} = Typography;

const discountCodeService = new DiscountCodeService();

// ── Helpers ────────────────────────────────────────────────────────────────────

const StatusTag = ({status}: {status: string}) => (
  <Tag color={status === 'active' ? 'success' : 'error'}>
    {status?.toUpperCase()}
  </Tag>
);

const ValueTag = ({value, type}: {value: number; type: string}) => (
  <Tag color="blue">
    {value}
    {type === 'PERCENTAGE' ? '%' : ' $'}
  </Tag>
);

// ── Component ─────────────────────────────────────────────────────────────────

export const DiscountCodeDetails = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const location = useLocation<{code?: NewDiscountCode}>();
  const {codeId} = useParams<{codeId: string}>();
  const {curriculums} = useDiscountCodeData();

  const [code, setCode] = useState<NewDiscountCode | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── Load ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fromState = location.state?.code;
    if (fromState) {
      loadCode(fromState);
    } else {
      discountCodeService
        .getDiscountCodeById(codeId)
        .then(loadCode)
        .catch(() => {
          setIsError(true);
          setIsFetching(false);
        });
    }
  }, [codeId]);

  const loadCode = (c: NewDiscountCode) => {
    setCode(c);
    form.setFieldsValue({
      value: c.value,
      type: c.type,
      status: c.status,
      expiryDate: moment(c.expiryDate),
      partnerId: c.partnerId,
      curriculumId: c.curriculumId,
    });
    setIsFetching(false);
  };

  // ── Save edit ─────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);

      const updateInput: NewUpdateDiscountCodeInput = {
        id: code!._id,
        value: values.value,
        type: values.type,
        status: values.status,
        expiryDate: values.expiryDate?.toISOString(),
        partnerId: values.partnerId || null,
        curriculumId: values.curriculumId || null,
      };

      const updated = await discountCodeService.updateDiscountCode(updateInput);
      setCode(updated);
      setIsEditing(false);

      notification.success({
        message: 'Code Updated',
        description: `"${updated.code}" has been updated successfully.`,
        duration: 4,
      });
    } catch (err: any) {
      if (err?.errorFields) return;
      notification.error({
        message: 'Update Failed',
        description: err?.message || 'An unexpected error occurred.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await discountCodeService.deleteDiscountCode(code!._id);
      notification.success({
        message: 'Code Deleted',
        description: `"${code!.code}" has been permanently deleted.`,
        duration: 3,
      });
      history.push('/discountcodes');
    } catch (err: any) {
      notification.error({
        message: 'Delete Failed',
        description: err?.message || 'An unexpected error occurred.',
      });
      setIsDeleting(false);
    }
  };

  // ── States ────────────────────────────────────────────────────────
  if (isFetching) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', padding: 80}}>
        <Spin size="large" tip="Loading discount code..." />
      </div>
    );
  }

  if (isError || !code) {
    return (
      <div style={{padding: 40}}>
        <Alert
          type="error"
          message="Failed to load discount code"
          description="The code may not exist or there was a network error."
          action={<Button onClick={() => history.goBack()}>Go Back</Button>}
        />
      </div>
    );
  }

  const isExpired = moment(code.expiryDate).isBefore(moment());

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div style={{padding: '24px 32px', maxWidth: 900, margin: '0 auto'}}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => history.goBack()}>
            Back
          </Button>
          <Title level={3} style={{margin: 0}}>
            <TagOutlined style={{marginRight: 8}} />
            <Text code style={{fontSize: 22}}>
              {code.code}
            </Text>
          </Title>
          <StatusTag status={code.status} />
          {isExpired && <Tag color="warning">EXPIRED</Tag>}
        </Space>

        <Space>
          {!isEditing ? (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => setIsEditing(true)}>
                Edit
              </Button>
              <Popconfirm
                title={`Delete "${code.code}"? This cannot be undone.`}
                okText="Yes, delete"
                okButtonProps={{danger: true}}
                cancelText="Cancel"
                onConfirm={handleDelete}>
                <Button danger icon={<DeleteOutlined />} loading={isDeleting}>
                  Delete
                </Button>
              </Popconfirm>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  setIsEditing(false);
                  loadCode(code);
                }}>
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={isSaving}
                onClick={handleSave}>
                Save Changes
              </Button>
            </>
          )}
        </Space>
      </div>

      {/* Details / Edit form */}
      {!isEditing ? (
        // ── Read-only view ──
        <Descriptions
          bordered
          column={{xs: 1, sm: 2}}
          size="middle"
          style={{marginBottom: 24}}>
          <Descriptions.Item label="Code">
            <Text code strong>
              {code.code}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Value">
            <ValueTag value={code.value} type={code.type} />
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            <Tag>{code.type === 'PERCENTAGE' ? '% Percentage' : '$ Fixed'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <StatusTag status={code.status} />
          </Descriptions.Item>
          <Descriptions.Item label="Expiry Date">
            <Tooltip
              title={moment(code.expiryDate).format('YYYY-MM-DD HH:mm:ss')}>
              <Text type={isExpired ? 'danger' : 'secondary'}>
                {moment(code.expiryDate).format('YYYY-MM-DD')} (
                {moment(code.expiryDate).fromNow()})
              </Text>
            </Tooltip>
          </Descriptions.Item>
          <Descriptions.Item label="Students Used">
            <Tag color={code.studentIds?.length > 0 ? 'orange' : 'default'}>
              {code.studentIds?.length ?? 0} student
              {code.studentIds?.length !== 1 ? 's' : ''}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Partner">
            {code.partnerId ? (
              <Tag color="purple">{code.partnerId}</Tag>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Curriculum">
            {code.curriculumId ? (
              <Tag color="geekblue">{code.curriculumId}</Tag>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Descriptions.Item>
          {code.packageIds?.length > 0 && (
            <Descriptions.Item label="Package IDs" span={2}>
              <Space wrap>
                {code.packageIds.map(id => (
                  <Tag key={id}>{id}</Tag>
                ))}
              </Space>
            </Descriptions.Item>
          )}
          {code.schoolId && (
            <Descriptions.Item label="School ID">
              <Text code>{code.schoolId}</Text>
            </Descriptions.Item>
          )}
          {code.classId && (
            <Descriptions.Item label="Class ID">
              <Text code>{code.classId}</Text>
            </Descriptions.Item>
          )}
          {code.levelId && (
            <Descriptions.Item label="Level ID">
              <Text code>{code.levelId}</Text>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Created By">
            <Tooltip title={code.createdBy}>
              <Text>{code.createdBy?.split('@')[0] ?? code.createdBy}</Text>
            </Tooltip>
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            <Text type="secondary">
              {moment(code.createdAt).format('YYYY-MM-DD HH:mm')}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            <Text type="secondary">{moment(code.updatedAt).fromNow()}</Text>
          </Descriptions.Item>
        </Descriptions>
      ) : (
        // ── Edit form ──
        <Form form={form} layout="vertical" style={{maxWidth: 640}}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="value"
                label="Discount Value"
                rules={[
                  {required: true, message: 'Value is required'},
                  {
                    type: 'number',
                    min: 0.01,
                    message: 'Must be greater than 0',
                  },
                ]}>
                <InputNumber style={{width: '100%'}} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Discount Type"
                rules={[{required: true}]}>
                <Select>
                  <Select.Option value="PERCENTAGE">% Percentage</Select.Option>
                  <Select.Option value="FIXED">$ Fixed</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{required: true}]}>
                <Select>
                  <Select.Option value="ACTIVE">Active</Select.Option>
                  <Select.Option value="EXPIRED">Expired</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="expiryDate"
                label="Expiry Date"
                rules={[{required: true, message: 'Required'}]}>
                <DatePicker
                  style={{width: '100%'}}
                  showTime
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="partnerId" label="Partner ID (optional)">
                <Select placeholder="None" allowClear>
                  {['SCHOOL', 'UNIVERSITY', 'COMPANY', 'OTHER'].map(p => (
                    <Select.Option key={p} value={p}>
                      {p}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="curriculumId" label="Curriculum ID (optional)">
                <Select placeholder="None" allowClear showSearch>
                  {curriculums.map(c => (
                    <Select.Option key={c.id} value={c.id!}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}

      {/* Student IDs table if any */}
      {code.studentIds?.length > 0 && (
        <div style={{marginTop: 24}}>
          <Title level={5}>
            Students who used this code ({code.studentIds.length})
          </Title>
          <Space wrap>
            {code.studentIds.map(id => (
              <Tag key={id} color="orange">
                {id}
              </Tag>
            ))}
          </Space>
        </div>
      )}
    </div>
  );
};
